import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type MenuItem = {
  key: string;
  label: string;
  onSelect: () => void;
  /** 填了就走内联二次确认：第一下把这一行换成这段问句，第二下才执行 */
  confirm?: string;
  danger?: boolean;
};

type Props = {
  items: MenuItem[];
  /** 触发位置（视口坐标，来自 contextmenu 事件的 clientX/Y） */
  x: number;
  y: number;
  onClose: () => void;
};

const EDGE = 8;

// 任务行的右键菜单。手写而不是用 antd 的 Dropdown —— /todo 刻意把 antd 挡在
// 外面（见 EntryList 的注释），为一个浮层把它拖回这一页不值当。
//
// 这个组件只管「怎么显示与操作一份菜单」：定位、翻转、关闭、键盘、二次确认。
// 「菜单里有哪几项、每项干什么」由调用方给 —— 那些是业务规则，放在持有
// handler 的 Todo.tsx 里只有一处。
const TaskContextMenu: React.FC<Props> = ({ items, x, y, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [active, setActive] = useState(0);
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  // 靠近视口右/下边缘时翻到另一侧，否则最后几行的菜单会跑到屏幕外。
  // useLayoutEffect 而不是 useEffect：要在浏览器绘制之前定好位，不然会看到
  // 菜单先出现在错的位置再跳一下。
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const { offsetWidth: w, offsetHeight: h } = el;
    setPos({
      left: x + w + EDGE > window.innerWidth ? Math.max(EDGE, x - w) : x,
      top: y + h + EDGE > window.innerHeight ? Math.max(EDGE, y - h) : y,
    });
  }, [x, y]);

  // 打开就把焦点收进来，键盘才有落点（右键是鼠标唤起的，但开着之后该能用键盘）。
  useEffect(() => {
    ref.current?.focus();
  }, []);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    // capture: true —— 中栏是自己的滚动容器，滚它不会触发 window 上的 scroll，
    // 不捕获阶段就收不到，菜单会浮在原地跟着列表错位。
    const onScroll = () => onClose();

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [onClose]);

  const choose = (item: MenuItem) => {
    if (item.confirm && confirmingKey !== item.key) {
      setConfirmingKey(item.key);
      return;
    }
    item.onSelect();
    onClose();
  };

  return (
    <div
      ref={ref}
      role="menu"
      tabIndex={-1}
      style={{ left: pos.left, top: pos.top }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive((n) => (n + 1) % items.length);
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive((n) => (n - 1 + items.length) % items.length);
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          const item = items[active];
          if (item) choose(item);
        }
      }}
      className="fixed z-40 min-w-[168px] overflow-hidden rounded-[9px] border border-todo-line bg-todo-card py-1 shadow-2xl outline-none"
    >
      {items.map((item, index) => {
        const confirming = confirmingKey === item.key;
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            onMouseEnter={() => setActive(index)}
            onClick={() => choose(item)}
            className={[
              'block w-full px-[13px] py-[6px] text-left text-[13px]',
              index === active ? 'bg-todo-card2' : '',
              confirming || item.danger ? 'text-todo-urgent' : 'text-todo-ink2',
            ].join(' ')}
          >
            {confirming ? item.confirm : item.label}
          </button>
        );
      })}
    </div>
  );
};

export default TaskContextMenu;
